import Add16 from '@carbon/icons-react/es/add/16';
import CaretDown16 from '@carbon/icons-react/es/caret--down/16';
import CaretUp16 from '@carbon/icons-react/es/caret--up/16';
import React, { FC, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { MemberType } from 'api/models';
import { Button } from 'components/atoms/button';
import { Collapse, Panel } from 'components/atoms/collapse';
import { Drawer as DrawerAtom } from 'components/atoms/drawer';
import { ShowForVendor } from 'components/atoms/user-type-utils';
import { SquadValue } from 'components/molecules/squad-picker';
import {
  AttachmentsTabViews,
  TaskAttachments,
} from 'components/molecules/tasks/details/attachments';
import { TaskDescription } from 'components/molecules/tasks/details/description';
import { PrivateNote } from 'components/molecules/tasks/details/private-note';
import { TaskFields } from 'components/molecules/tasks/details/task-fields';
import { TimeEntrySetting } from 'components/molecules/tasks/details/time-entry-settings';
import { SectionPanelKeyHash } from 'components/molecules/tasks/details/types';
import { updateDocumentTemplateTaskAction } from 'data/templates/documents';
import { useTemplateMentionsData } from 'data/templates/hooks';
import { ProjectTemplateTask } from 'data/templates/projects';
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import { useIsNativeUser } from 'hooks/use-user-type';
import {
  AttachmentsSectionPanel,
  Card,
  TaskFieldsSection,
  TemplateTaskHeader,
} from '../commons';

const Drawer = styled(DrawerAtom)`
  .ant-drawer-body {
    padding: 0;
  }

  ${Card} {
    min-height: 100%;
  }
`;

const activeCollapseKeys = Object.values(SectionPanelKeyHash);

export const MinifiedTaskModal: FC<{
  docId: number;
  resourceSectionId: number;
  task: ProjectTemplateTask;
  visible: boolean;
  onClose(): void;
}> = ({ docId, resourceSectionId, task, visible, onClose }) => {
  const dispatch = useDispatch();
  const [showPrivateNoteEditor, setPrivateNoteEditorVisibility] =
    useState<boolean>(!!task.privateTaskDescription?.length);
  const isNativeUser = useIsNativeUser();

  const updateTask = (payload) => {
    dispatch(
      updateDocumentTemplateTaskAction({
        docId,
        resourceSectionId,
        task: {
          ...task,
          ...payload,
        },
      }),
    );
  };

  const onUpdateName = (taskName: string) => updateTask({ taskName });
  const onUpdateAssignee = (assignee: SquadValue) => updateTask({ assignee });
  const onUpdateDescription = (taskDescription: string) =>
    updateTask({ taskDescription });
  const onUpdatePrivateNote = (privateTaskDescription: string) =>
    updateTask({ privateTaskDescription });
  const onUpdateTask = (_task: ProjectTemplateTask) => updateTask(_task);

  const onChangeResponsibility = (team: MemberType[]) =>
    updateTask({ responsibleOn: team });

  const [view, setView] = useState(AttachmentsTabViews.GridView);

  const { members, spaceTabs, tasks } = useTemplateMentionsData({
    forPrivateContext: task?.private,
  });

  const openPrivatePanel = () => {
    setPrivateNoteEditorVisibility(true);
  };
  const [timesheetsEnabled] = useAccountFeature(AccountFeature.Timesheets);

  return (
    <Drawer visible={visible} width={592} onClose={onClose}>
      <Card
        bordered={false}
        private={task.private}
        type="inner"
        title={
          <TemplateTaskHeader
            task={task}
            onUpdateName={onUpdateName}
            onUpdateAssignee={onUpdateAssignee}
            onChangeResponsibility={onChangeResponsibility}
          />
        }
      >
        <Collapse
          defaultActiveKey={activeCollapseKeys}
          expandIconPosition="right"
          expandIcon={({ isActive }) => {
            return isActive ? <CaretDown16 /> : <CaretUp16 />;
          }}
        >
          <Panel
            key={SectionPanelKeyHash.Description}
            header={<TaskDescription.Title />}
          >
            <TaskDescription.Editor
              taskDescription={task.taskDescription}
              mentionData={{
                members,
                spaceTabs,
                tasks: {
                  items: Object.values(tasks.items).filter(
                    (t) => t.taskId !== task.taskId,
                  ),
                },
              }}
              updateTask={onUpdateDescription}
            />
            <ShowForVendor>
              {!showPrivateNoteEditor ? (
                <Button
                  type="link"
                  size="small"
                  icon={<Add16 />}
                  onClick={openPrivatePanel}
                >
                  <FormattedMessage id="task.private_note.add" />
                </Button>
              ) : null}
            </ShowForVendor>
          </Panel>
          {showPrivateNoteEditor && isNativeUser ? (
            <Panel
              key={SectionPanelKeyHash.PrivateNote}
              header={<PrivateNote.Title />}
            >
              <PrivateNote.Editor
                mentionData={{
                  members,
                  spaceTabs,
                  tasks: {
                    items: Object.values(tasks.items).filter(
                      (t) => t.taskId !== task.taskId,
                    ),
                  },
                }}
                note={task.privateTaskDescription}
                onUpdate={onUpdatePrivateNote}
              />
            </Panel>
          ) : null}
          <Panel
            key={SectionPanelKeyHash.Attachments}
            header={<TaskAttachments.Title />}
            extra={
              Boolean(task.attachments?.length) && (
                <TaskAttachments.Tabs
                  taskId={task?.taskId}
                  onChooseTab={setView}
                />
              )
            }
          >
            <AttachmentsSectionPanel
              task={task}
              view={view}
              onUpdateTask={onUpdateTask}
            />
          </Panel>
          <Panel
            key={SectionPanelKeyHash.TaskFields}
            header={<TaskFields.Title />}
          >
            <TaskFieldsSection task={task} onUpdateTask={onUpdateTask} />
          </Panel>
          {timesheetsEnabled ? (
            <Panel
              key={SectionPanelKeyHash.TimeEntrySetting}
              header={<TimeEntrySetting.Title />}
            >
              <TimeEntrySetting.List task={task} onUpdateTask={onUpdateTask} />
            </Panel>
          ) : null}
        </Collapse>
      </Card>
    </Drawer>
  );
};
